import QRCode from 'qrcode';

interface TableQrSheetData {
    eventLabel: string;
    tables: string[];
    /** URL de base vers laquelle chaque QR pointe — le numéro/nom de table est ajouté en dernier segment. */
    baseUrl: string;
    /** Largeur/hauteur de la carte imprimée (titre + QR + nom de table), en mm — saisies séparément côté UI
     *  (QrCodesPage.tsx, en cm ou mm, toujours converties en mm avant d'arriver ici). Le QR lui-même reste
     *  carré (contrainte technique du format, sinon il devient illisible au scan) et se dimensionne pour
     *  tenir dans l'espace disponible une fois le titre et le nom de table réservés. Demande explicite
     *  d'Emeni (2026-08-23) : "pour la taille c'est hauteur et largeur que je veux". */
    cardWidthMm?: number;
    cardHeightMm?: number;
    /** Titre affiché au-dessus de chaque QR, modifiable par l'établissement — ex: "DÉCLAREZ VOS
     *  RESTRICTIONS ALIMENTAIRES" (demande explicite d'Emeni, 2026-08-22). */
    title?: string;
}

// Feuille imprimable de QR codes, un par table, à découper et poser sur chaque table le Jour J. Chaque QR
// pointe vers /ma-table/<token>/<numéro-de-table> — le convive scanne, choisit son nom dans une liste et se
// déclare assis à cette table (voir TableCheckinPage.tsx, Lot C). Mise en page en grille simple (jamais plus
// de colonnes que ce qui tient réellement à la taille choisie), une page peut contenir plusieurs tables. Un
// encadré en pointillés entoure chaque carte (titre + QR + nom de table) pour faciliter la découpe aux
// ciseaux — demande explicite d'Emeni (2026-08-22).
export const generateTableQrSheetPdf = async (data: TableQrSheetData) => {
    try {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        const title = (data.title || 'DÉCLAREZ VOS RESTRICTIONS ALIMENTAIRES').toUpperCase();
        const margin = 12;
        const gap = 10;
        const padding = 4;
        const titleH = 12;
        const labelH = 10;
        const cellW = Math.min(160, Math.max(30, data.cardWidthMm || 70));
        const cellH = Math.min(200, Math.max(40, data.cardHeightMm || 84));
        const qrSize = Math.max(10, Math.min(cellW - padding * 2, cellH - titleH - labelH - padding * 2));
        const pageW = 210, pageH = 297;
        const cols = Math.max(1, Math.floor((pageW - margin * 2 + gap) / (cellW + gap)));
        const rows = Math.max(1, Math.floor((pageH - margin * 2 + gap) / (cellH + gap)));
        const perPage = cols * rows;

        for (let i = 0; i < data.tables.length; i++) {
            const table = data.tables[i];
            const posOnPage = i % perPage;
            if (posOnPage === 0 && i > 0) doc.addPage();
            const col = posOnPage % cols;
            const row = Math.floor(posOnPage / cols);
            const cardX = margin + col * (cellW + gap);
            const cardY = margin + row * (cellH + gap);

            // Encadré de découpe en pointillés autour de toute la carte.
            doc.setDrawColor(150, 150, 150);
            doc.setLineDashPattern([1.5, 1.5], 0);
            doc.rect(cardX, cardY, cellW, cellH);
            doc.setLineDashPattern([], 0);

            const qrX = cardX + (cellW - qrSize) / 2;
            const qrY = cardY + padding + titleH;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9.5);
            doc.setTextColor(34, 25, 51);
            const titleLines = doc.splitTextToSize(title, cellW - padding * 2);
            doc.text(titleLines, cardX + cellW / 2, cardY + padding + 4, { align: 'center' });

            const url = `${data.baseUrl}/${encodeURIComponent(table)}`;
            const qrDataUrl = await QRCode.toDataURL(url, { margin: 1, width: 512 });
            doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(34, 25, 51);
            doc.text(`Table ${table}`, cardX + cellW / 2, qrY + qrSize + 8, { align: 'center' });
        }

        doc.save(`QR_tables_${(data.eventLabel || 'privatisation').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    } catch (e) {
        console.error('Table QR Sheet PDF Generation Error:', e);
        alert('Erreur lors de la génération des QR codes.');
    }
};
